import React from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import SafeIcon from '../../common/SafeIcon';

export default function KidCard({ kid, onQuickAction, onEdit }) {
  const navigate = useNavigate();
  const isNegative = kid.balance < 0;

  const handleContainerClick = (e) => {
    // If we clicked inside the card but not on a button, navigate
    navigate(`/kid/${kid.id}`);
  };

  const handleAction = (e, type) => {
    e.stopPropagation(); // CRITICAL: Stop navigation from firing
    onQuickAction(kid, type);
  };

  const handleEdit = (e) => {
    e.stopPropagation(); // CRITICAL: Stop navigation from firing
    onEdit(kid);
  };

  const getScheduleDisplay = () => {
    if (!kid.allowance_amount) return null;
    let dayStr = '';
    if (kid.allowance_interval === 'weekly' && kid.allowance_day) {
      dayStr = kid.allowance_day.charAt(0).toUpperCase() + kid.allowance_day.slice(1, 3);
    } else if (kid.allowance_interval === 'monthly' && kid.allowance_day) {
      const d = parseInt(kid.allowance_day);
      const suffix = (d % 10 === 1 && d !== 11) ? "st" : (d % 10 === 2 && d !== 12) ? "nd" : (d % 10 === 3 && d !== 13) ? "rd" : "th";
      dayStr = `${d}${suffix}`;
    }
    const timeStr = kid.allowance_time ? kid.allowance_time.substring(0, 5) : '';
    let parts = [];
    if (dayStr) parts.push(dayStr);
    if (timeStr) parts.push(`@ ${timeStr}`);
    return parts.length > 0 ? ` • ${parts.join(' ')}` : '';
  };

  return (
    <div 
      onClick={handleContainerClick}
      className="bg-white rounded-3xl p-5 shadow-sm border border-gray-100 cursor-pointer hover:shadow-xl hover:border-indigo-200 hover:-translate-y-1 transition-all group relative"
    >
      <div className="flex justify-between items-start mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-indigo-50 rounded-2xl flex items-center justify-center text-indigo-600 font-black text-xl group-hover:bg-indigo-600 group-hover:text-white transition-colors">
            {kid.name.charAt(0).toUpperCase()}
          </div>
          <div>
            <h3 className="font-black text-gray-900 leading-tight text-lg">{kid.name}</h3>
            {kid.allowance_amount && (
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider mt-0.5">
                ${kid.allowance_amount} {kid.allowance_interval}{getScheduleDisplay()}
              </p>
            )}
          </div>
        </div>
        <button 
          onClick={handleEdit}
          className="p-2 text-gray-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all"
          title="Edit Profile"
        >
          <SafeIcon icon="Settings" className="w-4 h-4" />
        </button>
      </div>

      <div className="mb-6">
        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Current Balance</p>
        <p className={`text-3xl font-black tracking-tighter ${isNegative ? 'text-red-500' : 'text-gray-900'}`}>
          {isNegative ? '-' : ''}${Math.abs(kid.balance).toFixed(2)}
        </p>
        
        {kid.last_allowance_paid_at && (
          <p className="text-[9px] text-gray-400 mt-2 flex items-center font-bold uppercase tracking-tight">
            <SafeIcon icon="Clock" className="w-2.5 h-2.5 mr-1" /> 
            Last paid: {format(new Date(kid.last_allowance_paid_at), 'MMM d, h:mm a')}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <button 
          onClick={(e) => handleAction(e, 'add')}
          className="flex items-center justify-center py-2.5 px-3 rounded-xl bg-green-50 text-green-700 text-xs font-black hover:bg-green-600 hover:text-white transition-all border border-green-100 shadow-sm"
        >
          <SafeIcon icon="Plus" className="w-3.5 h-3.5 mr-1.5" /> 
          FUNDS
        </button>
        <button 
          onClick={(e) => handleAction(e, 'subtract')}
          className="flex items-center justify-center py-2.5 px-3 rounded-xl bg-red-50 text-red-700 text-xs font-black hover:bg-red-600 hover:text-white transition-all border border-red-100 shadow-sm"
        >
          <SafeIcon icon="Minus" className="w-3.5 h-3.5 mr-1.5" /> 
          SPEND
        </button>
      </div>
    </div>
  );
}